import { useState } from 'react'

import { Button, TextInput } from '@atoms'
import { FormDialog, type FormDialogProps } from '@components/molecules/FormDialog'

import type { ComponentDoc, PropDocs } from '../types'

const props: PropDocs<FormDialogProps> = {
  children: {
    type: 'ReactNode',
    required: true,
    description: 'FormDialog.Title, then FormDialog.Form holding FormDialog.Body and FormDialog.Footer.',
  },
  open: { type: 'boolean', required: true, description: 'Whether the dialog shows. The parent owns it.' },
  onOpenChange: {
    type: '(open: boolean) => void',
    required: true,
    description: 'Called when Cancel, the close button, Escape or a click outside closes the dialog.',
  },
  size: { type: "'lg' | 'xl' | '2xl' | '3xl' | '4xl'", default: "'xl'", description: 'The widest the dialog grows.' },
  position: {
    type: "'middle' | 'top'",
    default: "'middle'",
    description:
      'Middle centres the dialog. Top pins its top edge, so the buttons stay put when an error makes the form taller.',
  },
  testId: { type: 'string', description: 'Sets data-testid on the dialog.' },
  preventOutsideClick: { type: 'boolean', default: 'false', description: 'Keeps the dialog open on a click outside.' },
  hasTinyMCE: {
    type: 'boolean',
    default: 'false',
    description: 'Lets the TinyMCE editor’s own pop-ups work inside the dialog. Escape no longer closes it.',
  },
}

function NameDialog() {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  return (
    <>
      <Button onClick={() => setOpen(true)}>Add call list</Button>
      <FormDialog.Root open={open} onOpenChange={setOpen} size="lg" position="top">
        <FormDialog.Title>Add call list</FormDialog.Title>
        <FormDialog.Form
          onSubmit={(event) => {
            event.preventDefault()
            setOpen(false)
          }}
        >
          <FormDialog.Body>
            <TextInput
              name="name"
              label="Name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </FormDialog.Body>
          <FormDialog.Footer submitText="Create call list" />
        </FormDialog.Form>
      </FormDialog.Root>
    </>
  )
}

const doc: ComponentDoc<FormDialogProps> = {
  slug: 'form-dialog',
  name: 'FormDialog',
  tier: 'molecule',
  description: 'A dialog that holds a short form, with Cancel and a submit button pinned to the bottom.',
  importStatement: "import { FormDialog } from '@components/molecules/FormDialog'",
  sourcePath: 'app/frontend/components/molecules/FormDialog.tsx',
  props,
  examples: [
    {
      title: 'A one-field form',
      note: 'The page keeps the open state and renders the button that opens it.',
      render: () => <NameDialog />,
      code: `const [open, setOpen] = useState(false)

<Button onClick={() => setOpen(true)}>Add call list</Button>
<FormDialog.Root open={open} onOpenChange={setOpen} size="lg" position="top">
  <FormDialog.Title>Add call list</FormDialog.Title>
  <FormDialog.Form onSubmit={submit}>
    <FormDialog.Body>
      <TextInput name="name" label="Name" required errors={errors} value={name} onChange={changeName} />
    </FormDialog.Body>
    <FormDialog.Footer submitText="Create call list" isLoading={busy} />
  </FormDialog.Form>
</FormDialog.Root>`,
    },
  ],
  usage: {
    do: [
      'Use it for a short form that leaves the user on the same page or opens the thing it made.',
      'Name the submit button after what it does, like "Create call list".',
      'Set isLoading on the footer while the request runs.',
      'Show each server error under its field. Show any other error in an Alert at the top of the body.',
      'Use position="top" when errors can make the form taller.',
    ],
    dont: [
      'Build a form dialog from Dialog.Content, a form and Dialog.Footer by hand.',
      'Use it for a long form or several steps. Give those their own page.',
      'Confirm a destructive action with it. Use ConfirmDialog.',
    ],
  },
  a11y: [
    'Focus moves to the first field when the dialog opens and goes back to the button when it closes.',
    'Escape closes the dialog unless hasTinyMCE is set.',
    'The title names the dialog for screen readers.',
  ],
}

export default doc
